<template>
	<div class="experience-timeslot-edit">
		<ModalHeader :title="getString('edit_timeslot')" :button-text="isTimeslotNew ? getString('add') : getString('edit')" @click-button="confirmAction" />
		<AppList :items="itemsScheduleEdit" >
			<template v-slot:slot-time="{item}">
				<div class="slot-time row space-between centered">
					<div class="item-title">{{ item.title }}</div>
					<div class="slots-from-container">
						<AppTextInput type="time" :ref="'input-time-from-'" :label="getString('from')" placeholder="12:00" :value="timeslotStart" @changed-text="v => timeslotStart = v" />
						<AppTextInput type="time" :ref="'input-time-to-'" :label="getString('to')" placeholder="14:00" :value="timeslotEnd" @changed-text="v => timeslotEnd = v" />
					</div>
				</div>
			</template>
			<template #list-slot="{item}">
				<AppList :items="item.subitems" />
			</template>
		</AppList>
	</div>
</template>

<script>
import AppList from "@/shared/components/AppList.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useExperiencesStore, useUserStore} from "@/console-new/stores";

export default {
	setup() {
		const experiencesStore = useExperiencesStore();
		const notificationStore = useNotificationStore();
		const userStore = useUserStore();
		return {
			experiencesStore,
			notificationStore,
			userStore,
			getString: useLanguageStore().getString,
		};
	},
	name: "ExperienceTimeslotEdit" ,
	components: {ModalHeader, AppTextInput, AppList},
	emits: ['close', 'delete', 'add', 'edit'],
	props: {
		timeslot: {
			type: Object,
			required: true
		},
		timeslots: {
			type: Array,
			required: true
		}
	},
	data() {
		return {
			timeslotName: '',
			timeslotPrice: null,
			timeslotStart: '',
			timeslotEnd: '',
			timeslotTime: 0,
			timeslotIntervals: 0,
			timeslotDontRequestCard: false,
			timeslotMaxPeople: null,
			hasEdited: false,
		}
	},
	computed: {
		isTimeslotNew() {
			return (this.timeslot.experience_timeslot_id+"").startsWith('new');
		},
		itemsScheduleEdit() {
			let result = [];

			result.push({
				title: this.getString('timeslot_name'),
				textInput: true,
				value: this.timeslotName,
				placeholder: this.getString('timeslot_name'),
				callbackText: (v) => {
					this.hasEdited = true;
					this.timeslotName = v;
				},
			});
			result.push({
				title: this.getString('custom_price'),
				switch: true,
				dropdownSlot: 'list-slot',
				active: this.timeslotPrice !== null,
				callback: (v) => {
					this.hasEdited = true;
					this.timeslotPrice = v ? 0 : null;
				},
				subitems: [
					{
						title: '',
						numberInput: true,
						min: 0,
						symbol: '€',
						rightSymbol: true,
						value: this.timeslotPrice !== null ? this.timeslotPrice / 100 : 0,
						callbackNumber: (v) => {
							this.hasEdited = true;
							if(v !== 0) {
								this.timeslotPrice = v*100;
							} else {
								this.timeslotPrice = null;
							}
						},
					},
				],
			});
			result.push({
				title: this.getString('schedule'),
				customSlot: 'slot-time',
			});
			result.push({
				title: this.getString('reservation_time'),
				explain: this.getString('reservation_time_explain'),
				symbol: 'm',
				rightSymbol: true,
				numberInput: true,
				min: 0,
				max: 300,
				value: this.timeslotTime,
				callbackNumber: (v) => {
					this.hasEdited = true;
					this.timeslotTime = v;
				},
			});
			result.push({
				title: this.getString('slot_intervals'),
				explain: this.getString('slot_intervals_explain'),
				symbol: 'm',
				rightSymbol: true,
				numberInput: true,
				min: 0,
				max: 300,
				value: this.timeslotIntervals,
				callbackNumber: (v) => {
					this.hasEdited = true;
					this.timeslotIntervals = v;
				},
			});

			if (this.userStore.currBusiness && this.userStore.currBusiness.reservations_request_card) {
				result.push({
					title: this.getString('dont_request_card'),
					switch: true,
					active: this.timeslotDontRequestCard,
					callback: (v) => {
						this.hasEdited = true;
						this.timeslotDontRequestCard = v;
					},
				});
			}

			result.push({
				title: this.getString('max_people_timeslot'),
				explain: this.getString('max_people_timeslot_explain'),
				switch: true,
				dropdownSlot: 'list-slot',
				active: this.timeslotMaxPeople !== null,
				callback: (v) => {
					this.hasEdited = true;
					this.timeslotMaxPeople = v ? 20 : null;
				},
				subitems: [
					{
						title: '',
						numberInput: true,
						min: 1,
						max: 999,
						value: this.timeslotMaxPeople !== null ? this.timeslotMaxPeople : 20,
						callbackNumber: (v) => {
							this.hasEdited = true;
							this.timeslotMaxPeople = v > 0 ? v : null;
						},
					},
				],
			});

			result.push({
				title: this.getString('delete_timeslot'),
				buttonTitle: this.getString('delete'),
				callbackButton: () => {
					this.$emit('delete');
				},
			});

			return result;
		}
	},
	methods: {
		confirmAction() {
			if(this.timeslotName.length < 1) {
				this.notificationStore.showNotification("invalid-name");
				return;
			}
			let timeStart = this.timeslotStart.split(':');
			let timeEnd = this.timeslotEnd.split(':');
			if(timeStart.length !== 2 || timeEnd.length !== 2) {
				this.notificationStore.showNotification("timetable-invalid-time");
				return;
			}
			if(parseInt(timeStart[0]) > parseInt(timeEnd[0]) || (parseInt(timeStart[0]) === parseInt(timeEnd[0]) && parseInt(timeStart[1]) >= parseInt(timeEnd[1]))) {
				this.notificationStore.showNotification("timetable-invalid-time");
				return;
			}

			let duration = (parseInt(timeEnd[0]) - parseInt(timeStart[0]))*60 + parseInt(timeEnd[1]) - parseInt(timeStart[1]);
			if(this.timeslotTime <= 0 || this.timeslotTime > duration) {
				this.notificationStore.showNotification("invalid-timeslot-time");
				return;
			}
			if(this.timeslotIntervals <= 0 || this.timeslotIntervals > duration) {
				this.notificationStore.showNotification("invalid-timeslot-interval");
				return;
			}

			// check if timeslot overlaps with another
			for(let i = 0; i < this.timeslots.length; i++) {
				let slot = this.timeslots[i];
				if(slot.weekday === this.timeslot.weekday && slot.experience_timeslot_id !== this.timeslot.experience_timeslot_id) {
					let slotStart = slot.start.split(':');
					let slotEnd = slot.end.split(':');
					if(parseInt(timeStart[0]) < parseInt(slotEnd[0]) || (parseInt(timeStart[0]) === parseInt(slotEnd[0]) && parseInt(timeStart[1]) < parseInt(slotEnd[1]))) {
						if(parseInt(timeEnd[0]) > parseInt(slotStart[0]) || (parseInt(timeEnd[0]) === parseInt(slotStart[0]) && parseInt(timeEnd[1]) > parseInt(slotStart[1]))) {
							this.notificationStore.showNotification("timetable-overlaps");
							return;
						}
					}
				}
			}


			if(this.isTimeslotNew) {

				this.$emit('add');

			} else {
				this.timeslot.name = this.timeslotName;
				this.timeslot.price = this.timeslotPrice;
				this.timeslot.start = this.timeslotStart;
				this.timeslot.end = this.timeslotEnd;
				this.timeslot.time = this.timeslotTime;
				this.timeslot.intervals = this.timeslotIntervals;
				this.timeslot.dontRequestCard = this.timeslotDontRequestCard;
				this.timeslot.maxPeople = this.timeslotMaxPeople;

				if(this.hasEdited) {
					this.$emit('edit');
				}

				this.$emit('close');
			}
		}
	},
	mounted() {
		this.hasEdited = false;
		if(this.timeslot) {
			this.timeslotName = this.timeslot.name || '';
			this.timeslotPrice = this.timeslot.price ?? null;
			this.timeslotStart = this.timeslot.start || '';
			this.timeslotEnd = this.timeslot.end || '';
			this.timeslotTime = this.timeslot.time || 0;
			this.timeslotIntervals = this.timeslot.intervals || 0;
			this.timeslotDontRequestCard = this.timeslot.dontRequestCard || false;
			this.timeslotMaxPeople = this.timeslot.maxPeople ?? null;
		}
	}
}
</script>

<style scoped lang="scss">
.experience-timeslot-edit {
	.slots-from-container {
		display: flex;
		gap: 10px;

		.input-wrapper {
			max-width: 120px;
		}
	}
}
</style>